import { memo, type MouseEvent } from 'react';
import { GalleryOrientation } from './ProductGallery';
import DOMPurify from 'dompurify';
import { useAnalytics } from './hooks/useAnalytics';
import { AnalyticsEvent } from './constants/analyticsEvents';
import { getEnv } from '../utils/env';

interface Props {
  productId: string;
  currency: string;
  title: string;
  description: string;
  imageUrl: string;
  productUrl: string;
  price: string;
  salePrice: string;
  productRange: string;
  decimalPlaces?: number;
  brandColor?: string;
  galleryOrientation: GalleryOrientation;
}

const storeHostName = getEnv('STORE_HOST_NAME');
export const ProductTile = memo(
  ({
    productId,
    title,
    description,
    imageUrl,
    productUrl,
    salePrice,
    price,
    productRange,
    currency = '£',
    decimalPlaces = 2,
    brandColor,
    galleryOrientation = GalleryOrientation.HORIZONTAL,
  }: Props) => {
    const isGalleryHorizontal =
      galleryOrientation === GalleryOrientation.HORIZONTAL;

    const shouldShowSalePrice =
      salePrice && salePrice.length > 0 && Number(salePrice) < Number(price);

    const { trackEvent } = useAnalytics();

    const productUrlWithStaticHost = storeHostName
      ? `https://${storeHostName}${productUrl}`
      : productUrl;

    const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      trackEvent(AnalyticsEvent.PRODUCT_DETAILS_VIEWED, { productId });
      window.open(productUrlWithStaticHost, '_self');
    };

    return (
      <a
        onClick={onClick}
        href={productUrlWithStaticHost}
        className={`superbar-product-tile-link caddietw-text-blue-500 caddietw-auto caddietw-text-sm caddietw-font-semibold caddietw-leading-snug ${
          isGalleryHorizontal ? 'caddietw-pb-1' : ''
        } caddietw-w-full`}
        rel="noreferrer"
      >
        <div
          className={`caddietw-flex ${
            isGalleryHorizontal
              ? 'caddietw-p-0 caddietw-flex-col caddietw-justify-between caddietw-w-full'
              : 'caddietw-pt-[24px] caddietw-pb-[28px] caddietw-px-[20px] caddietw-gap-7'
          } caddietw-h-full caddietw-box-border`}
        >
          <div
            className={`superbar-product-image ${
              isGalleryHorizontal ? 'caddietw-w-full' : 'caddietw-w-[280px]'
            } caddietw-min-h-[170px] md:caddietw-max-h-[260px] lg:caddietw-max-h-[330px] caddietw-overflow-hidden`}
          >
            <img
              className="caddietw-rounded-md caddietw-w-full caddietw-h-full caddietw-object-contain"
              src={imageUrl}
              alt={title}
              //For higher specificity in the css so that customer css does not override it
              style={{
                maxHeight: '100%',
                height: 'auto',
                width: '100%',
              }}
            />
          </div>
          <div className="caddietw-w-full">
            <div
              className={`caddietw-w-full ${
                isGalleryHorizontal ? 'caddietw-mt-4' : ''
              }`}
            >
              <div
                id={'caddie-tile-product-range'}
                className={`caddietw-text-sm caddietw-font-semibold caddie-tile-product-range ${
                  brandColor ? '' : 'caddietw-text-[#4BABA1]'
                }`}
                style={{
                  color: brandColor ? brandColor : '#4BABA1',
                }}
              >
                {productRange?.toUpperCase()}
              </div>
              <div className="caddietw-flex caddietw-flex-col caddietw-justify-between">
                <div
                  className="superbar-product-tile-title caddietw-font-semibold caddietw-text-black caddietw-text-sm caddietw-w-full caddietw-line-clamp-2 caddietw-text-wrap"
                  id={'caddie-tile-product-title'}
                >
                  {title}
                </div>
              </div>
              {!isGalleryHorizontal && (
                <div
                  className={
                    'caddietw-text-sm caddietw-text-[#707072] caddietw-line-clamp-5 caddietw-mt-1 caddie-product-tile-description'
                  }
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description),
                  }}
                ></div>
              )}
            </div>
            <div className={'caddietw-w-full caddietw-flex caddietw-mt-4'}>
              {shouldShowSalePrice ? (
                <div
                  id={'caddie-tile-sale-price'}
                  className="caddietw-font-bold caddietw-text-sm caddietw-text-[#FF4233]"
                >
                  {`${currency}${Number(salePrice).toFixed(2)}`}
                </div>
              ) : null}
              {price.length ? (
                <div
                  id={'caddie-tile-regular-price'}
                  className={`caddietw-font-bold caddietw-text-sm ${
                    shouldShowSalePrice
                      ? 'caddietw-line-through caddietw-ml-2 caddietw-text-black'
                      : 'caddietw-text-black'
                  }`}
                >
                  {currency}
                  {decimalPlaces
                    ? Number(price).toFixed(decimalPlaces)
                    : Number(price)}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </a>
    );
  }
);
